"use client";

import { COMMUNITY_SEARCH_API } from "@/lib/apis";
import { cn } from "@/lib/utils";
import { TCommunityPostSearchRecord, TCommunityPostSearchResponseDTO } from "@/types/post";
import { Loader2Icon, SearchIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

const CommunitySearch = () => {
	const [query, setQuery] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [results, setResults] = useState<TCommunityPostSearchRecord[]>([]);
	const [hasSearched, setHasSearched] = useState<boolean>(false);

	const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			setIsLoading(true);
			setHasSearched(true);

			const url = new URL(COMMUNITY_SEARCH_API);

			url.searchParams.set("query", query);

			const response = await fetch(url);
			const data = (await response.json()) as TCommunityPostSearchResponseDTO;

			const posts = data?.data?.posts || [];

			setResults(posts);
		} catch (error) {
			console.log("Error occured while fetching community search results", error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setHasSearched(false);
		setQuery(e.target.value);
	};

	return (
		<form onSubmit={handleSearch}>
			<div className="group relative flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border border-solid border-gray-100 bg-white py-2 pl-4 pr-2 text-base shadow-xl">
				<div className="my-auto flex items-center gap-2 self-stretch">
					<SearchIcon className="h-5 w-5" />

					<label htmlFor="search-input" className="sr-only">
						What are you looking for?
					</label>

					<input
						type="text"
						id="search-input"
						placeholder="Search for posts"
						onChange={handleOnChange}
						className="my-auto w-full self-stretch border-none bg-transparent py-3 placeholder:text-gray-400 focus:outline-none"
					/>
				</div>

				<button
					type="submit"
					className="my-auto block w-full gap-2 self-stretch overflow-hidden rounded-lg border border-solid border-blue-700 bg-blue-700 px-5 py-3 font-semibold text-white shadow-sm md:w-fit"
				>
					{isLoading ? (
						<Loader2Icon className="mx-auto h-4 w-4 animate-spin text-white md:h-6 md:w-6" />
					) : (
						"Explore Search"
					)}
				</button>

				<div
					className={cn(
						"customized-scrollbar absolute left-0 top-[105%] z-20 hidden max-h-80 w-full overflow-y-scroll rounded-md bg-white p-4 text-left shadow-lg",
						query !== "" ? "group-focus-within:block" : ""
					)}
				>
					<ul>
						<h4 className="mb-2 border-b pb-2 text-xs font-bold uppercase tracking-wider text-gray-600">
							Posts
						</h4>

						{results.length > 0 ? (
							results.map((result) => (
								<li key={result.id} className="mb-2">
									<Link
										href={`/communities/${result.community.slug}/${result.channel.slug}/${result.slug}`}
										className="flex items-center justify-start gap-2 text-gray-500 hover:text-brand-primary focus:text-brand-primary"
									>
										<span>
											<SearchIcon className="h-3 w-3" />
										</span>

										<span className="text-xs">{result.title}</span>
									</Link>
								</li>
							))
						) : hasSearched && !isLoading && results.length === 0 ? (
							<li className="text-xs text-gray-400">No results found</li>
						) : (
							<li className="text-xs text-gray-400">
								{isLoading ? "..." : `Click 'Explore Search' to find posts`}
							</li>
						)}
					</ul>
				</div>
			</div>
		</form>
	);
};

export default CommunitySearch;
